import React, { useState, useEffect } from 'react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2, Linkedin, Mail, Globe, Sun, Moon, ArrowLeft, Plus, Minus, ShoppingBag } from 'lucide-react';
import Logo from '../../components/Logo';
import { useCart } from '../../hooks/useCart';
import SectionHeading from './components/SectionHeading';
import { smoothScrollIntoView } from '../../utils/smoothScroll';

const FAQItem = React.memo(({ item, color }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="border-b border-border-color last:border-0">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="w-full py-8 flex items-center justify-between text-left group transition-all"
      >
        <h5 
          className="text-xl md:text-2xl font-black tracking-tight flex-1 pr-8 transition-colors duration-300"
          style={{ color: (isOpen || isHovered) ? color : 'inherit' }}
        >
          {item.q}
        </h5>
        <div 
          className="w-10 h-10 rounded-full border border-border-color flex items-center justify-center transition-all duration-500 group-hover:scale-110"
          style={{ borderColor: isOpen ? color : 'inherit', backgroundColor: isOpen ? `${color}10` : 'rgba(255, 255, 255, 0.03)' }}
        >
          {isOpen ? <Minus size={18} style={{ color }} /> : <Plus size={18} style={{ color: isHovered ? color : 'inherit' }} />}
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <Motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-8 text-lg text-text-muted font-light leading-relaxed max-w-4xl">
              {item.a}
            </div>
          </Motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

const LAYOUT_TRANSLATIONS = {
  de: {
    back: "Zurück",
    configure: "LIVE DEMO",
    slots: "Slots",
    available: "Freie Sprint Slots",
    total: "Gesamt",
    explore: {
      title: "Noch Mehr",
      highlight: "Booster",
      label: "Sammlung"
    },
    faq: {
      title: "Häufige",
      highlight: "Fragen",
      label: "Keine Reibung",
      items: [
        {
          q: "Was kostet der KI Voice Agent pro Monat?",
          a: "Die Einrichtung kostet einmalig 3.999 €. Danach zahlst du einen festen Monatspreis: Starter 79 €, Professional 199 € oder Enterprise 499 € pro Monat, jährlich jeweils günstiger. Zusätzliche Minuten kosten 0,10 €. Der Kalkulator auf dieser Seite zeigt dir, wie schnell sich das über gewonnene Anrufe und eingesparte Telefonzeit rechnet. Alle Preise zuzüglich Mehrwertsteuer."
        },
        {
          q: "Wie lange dauert die Implementierung?",
          a: "Der Standard-Sprint dauert 10 Werktage vom Onboarding bis zum Live-Go. In dieser Zeit baut CRE8NOW die Persönlichkeit, trainiert das Modell auf deine Daten, richtet alle Integrationen ein und führt Live-Simulationen durch. Du bist jederzeit eingebunden, kein Blackbox-Prozess."
        },
        {
          q: "Klingt der Agent wirklich menschlich?",
          a: "Ja. CRE8NOW nutzt modernste Voice-Clone-Technologie kombiniert mit einem auf deine Branche feinjustierten Sprachmodell. Der Agent kennt dein Angebot, deine Preise und typische Einwände. In Tests erkannten über 85 % der Anrufer nicht, dass sie mit einer KI gesprochen haben, bis CRE8NOW es ihnen sagte."
        },
        {
          q: "Welche Systeme kann der Agent integrieren?",
          a: "HubSpot, Salesforce, Pipedrive, Google Kalender, Outlook, Calendly, benutzerdefinierte Webhooks, Zapier und viele mehr. Wenn du ein spezifisches System nutzt, das nicht auf dieser Liste steht, prüft CRE8NOW die Integration kostenlos im Onboarding-Call."
        },
        {
          q: "Wer besitzt den Agenten und alle Daten?",
          a: "Du. 100 %. Nach der Abschlusszahlung übergibt CRE8NOW alle Konfigurationsdateien, Trainingsdaten und System-Zugänge. Deine Gesprächsdaten werden ausschließlich auf von dir kontrollierten Infrastrukturen gespeichert, DSGVO-konform."
        },
        {
          q: "Was passiert, wenn ich nach dem Sprint Änderungen brauche?",
          a: "Jeder Sprint beinhaltet 2 Runden schneller Revisionen direkt nach dem Launch. Für laufende Optimierungen, neue Wissensinhalte oder Kapazitätserweiterung bietet CRE8NOW Wartungspakete ab €99/Monat an."
        }
      ]
    },
    footer: {
      home: "Home",
      imprint: "Impressum",
      privacy: "Datenschutz"
    },
    slotsLeft: "Slots frei"
  },
  en: {
    back: "Back to Lab",
    configure: "LIVE DEMO",
    slots: "Slots",
    available: "Available sprint slots",
    total: "Total",
    explore: {
      title: "Deals",
      highlight: "Explore",
      label: "The Collection"
    },
    faq: {
      title: "Frequently Asked",
      highlight: "Questions",
      label: "Removing the Friction",
      items: [
        {
          q: "What does the AI Voice Agent cost per month?",
          a: "Setup is a one-time €3,999. After that you pay a fixed monthly price: Starter €79, Professional €199 or Enterprise €499 per month, each cheaper when billed yearly. Additional minutes cost €0.10. The calculator on this page shows how quickly that pays for itself through answered calls and saved phone time. All prices exclude VAT."
        },
        {
          q: "How long does implementation take?",
          a: "The standard sprint runs 10 business days from onboarding to go-live. During this time, CRE8NOW builds the personality, trains the model on your data, configures all integrations, and runs live simulations. You are included at every stage, no black-box process."
        },
        {
          q: "Does the agent actually sound human?",
          a: "Yes. CRE8NOW uses state-of-the-art voice-clone technology combined with a language model fine-tuned for your industry. The agent knows your offer, your pricing, and common objections. In tests, over 85% of callers did not realize they were speaking with an AI, until CRE8NOW told them."
        },
        {
          q: "Which systems can the agent integrate with?",
          a: "HubSpot, Salesforce, Pipedrive, Google Calendar, Outlook, Calendly, custom webhooks, Zapier, and many more. If you use a specific system not on this list, CRE8NOW assesses the integration free of charge during the onboarding call."
        },
        {
          q: "Who owns the agent and all data?",
          a: "You do. 100%. Upon final payment, CRE8NOW hands over all configuration files, training data, and system credentials. Your call data is stored exclusively on infrastructure you control, fully GDPR-compliant."
        },
        {
          q: "What if I need changes after the sprint?",
          a: "Every sprint includes 2 rounds of rapid revisions immediately after launch. For ongoing optimizations, new knowledge content, or capacity expansion, CRE8NOW offers maintenance packages from €99/month."
        }
      ]
    },
    footer: {
      home: "Home",
      imprint: "Imprint",
      privacy: "Privacy"
    },
    slotsLeft: "Slots Left"
  }
};

const getCampaignView = (campaignId) => {
  if (campaignId === 'vibe-coding') {
    return 'campaign-rapid-prototyping';
  }

  return `campaign-${campaignId}`;
};

const CampaignLayout = ({ 
  children, 
  category, 
  slotsRemaining, 
  totalSlots, 
  theme = 'dark', 
  setTheme,
  color = 'var(--design-primary)',
  setView,
  campaigns = [],
  currentId,
  lang,
  setLang,
  onInquiry,
  faqItems,
  navItems = []
}) => {
  const { cartItems, setIsCartOpen } = useCart();
  const [lastBooking, setLastBooking] = useState(null);
  const [activeSection, setActiveSection] = useState('');
  const lt = LAYOUT_TRANSLATIONS[lang] || LAYOUT_TRANSLATIONS.en;

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-150px 0px -40% 0px', // Trigger when section is in top half
      threshold: 0
    };

    const handleIntersect = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        } else if (activeSection === entry.target.id) {
          // If we leave a section and it was active, clear it 
          // (helps avoid stickiness during hero/video transitions)
          setActiveSection('');
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, options);

    navItems.forEach(item => {
      const element = document.getElementById(item.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [navItems]);

  useEffect(() => {
    // Social Proof Urgency Logic
    const initSocialProof = () => {
      const lastBookingTime = localStorage.getItem(`last-booking-${category}`);
      if (lastBookingTime) {
        const mins = Math.floor((Date.now() - parseInt(lastBookingTime)) / 60000);
        setLastBooking(mins > 0 ? mins : 1);
      } else {
        const randomMins = Math.floor(Math.random() * 20) + 2;
        localStorage.setItem(`last-booking-${category}`, (Date.now() - (randomMins * 60000)).toString());
        setLastBooking(randomMins);
      }
    };

    initSocialProof();
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0); 
    }
  }, [category]);

  return (
    <div className={`min-h-screen bg-bg-color text-text-color font-sans ${theme === 'light' ? 'light' : 'dark'}`}>
      {/* HEADER ALIGNED WITH LANDINGPAGE */}
      <Motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-[100] py-2 md:py-3"
      >
        <div className="w-full px-2 md:px-3 flex items-center justify-between gap-2 md:gap-3 relative">
          {/* Logo / Back */}
          <div className="flex-1 flex justify-start items-center">
            <button 
              onClick={() => setView('home')}
              aria-label={lang === 'de' ? 'Zurück zur Startseite' : 'Back to Home'}
              className="flex items-center gap-1 cursor-pointer bg-bg-color/80 backdrop-blur-md rounded-[4px] px-1 py-1 md:px-4 md:py-3 group transition-all duration-500 hover:border-text-color/20 border border-transparent h-11 md:h-14"
            >
              <Logo theme={theme} activeTab={category?.toLowerCase().includes('ai') ? 'ai' : 'design'} className="h-8 md:h-8 w-auto" />
            </button>
          </div>

          {/* Desktop/Mobile navigation tabs - in flow */}
          <div className="md:hidden absolute inset-0 flex justify-center items-center pointer-events-none">
            <div className={`pointer-events-auto flex bg-bg-color/80 rounded-[10px] p-1 backdrop-blur-md gap-1 transition-all duration-500 h-11 border border-text-color/10`}>
              {['design', 'ai', 'concierge'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    if (tab === 'concierge') {
                      setView('campaign-ai-voice-agent');
                      window.scrollTo(0, 0);
                      return;
                    }
                    setView('home');
                    // Give LandingPage.jsx a small frame to mount, then set the activeTab
                    setTimeout(() => {
                      const triggerTabClick = () => {
                        const btn = document.querySelector(`[layoutid="navTab"]`);
                        if (btn) {
                          const event = new CustomEvent('set_tab', { detail: tab });
                          window.dispatchEvent(event);
                        }
                      };
                      triggerTabClick();
                    }, 50);
                  }}
                  className={`px-3 md:px-6 rounded-[8px] ui-text font-black uppercase transition-all duration-300 relative flex items-center justify-center ${
                    category === 'AI Voice Agent' && tab === 'concierge' ? 'text-white' : 'text-text-color'
                  }`}
                >
                  {category === 'AI Voice Agent' && tab === 'concierge' && (
                    <Motion.div 
                      layoutId="navTab"
                      className="absolute inset-0 rounded-[8px]"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      style={{ backgroundColor: color }}
                    />
                  )}
                  <span className="relative z-10 inline-block">
                    {tab === 'ai' ? 'AI' : (tab === 'concierge' ? 'Concierge' : 'Design')}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="hidden md:flex bg-bg-color/80 rounded-[4px] p-1 backdrop-blur-md gap-1 transition-all duration-500 h-11 border border-text-color/10">
            {['design', 'ai', 'concierge'].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  if (tab === 'concierge') {
                    setView('campaign-ai-voice-agent');
                    window.scrollTo(0, 0);
                    return;
                  }
                  setView('home');
                  setTimeout(() => {
                    const event = new CustomEvent('set-landing-tab', { detail: tab });
                    window.dispatchEvent(event);
                  }, 50);
                }}
                className={`px-6 rounded-[4px] ui-text font-black uppercase transition-all duration-300 relative flex items-center justify-center ${
                  category === 'AI Voice Agent' && tab === 'concierge' ? 'text-white' : 'text-text-color'
                }`}
              >
                {category === 'AI Voice Agent' && tab === 'concierge' && (
                  <Motion.div 
                    layoutId="navTabDesktop"
                    className="absolute inset-0 rounded-[4px]"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    style={{ backgroundColor: color }}
                  />
                )}
                <span className="relative z-10 inline-block">
                  {tab === 'ai' ? 'AI' : (tab === 'concierge' ? 'Concierge' : 'Design')}
                </span>
              </button>
            ))}
          </div>

          {/* Right: Actions */}
          <div className="flex-1 flex-row flex justify-end items-center gap-1.5 md:gap-3">
            {/* Desktop Language Toggle */}
            <div className="hidden lg:flex bg-bg-color/95 backdrop-blur-md rounded-[4px] border border-text-color/10 p-1">
              {['de', 'en'].map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`relative z-10 px-2 md:px-3 py-1 md:py-1.5 rounded-[2px] text-[9px] md:text-[10px] font-black uppercase transition-all duration-300 ${lang === l ? 'text-white' : 'text-text-muted hover:text-text-color'}`}
                >
                  <span className="relative z-10">{l}</span>
                  {lang === l && (
                    <Motion.div
                      layoutId="langTab"
                      className="absolute inset-0 z-[-1] rounded-[2px]"
                      style={{ backgroundColor: color }}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                </button>
              ))}
            </div>

            <Motion.button 
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              aria-label={lang === 'de' ? 'Design-Modus umschalten' : 'Toggle Theme'}
              className="hidden md:flex bg-bg-color/95 backdrop-blur-md rounded-[4px] hover:bg-white/[0.08] transition-all duration-500 items-center justify-center w-10 h-10 md:w-14 md:h-14 border border-text-color/10"
            >
              <AnimatePresence mode="wait">
                <Motion.div
                  key={theme}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {theme === 'dark' ? <Sun size={20} style={{ color }} /> : <Moon size={20} style={{ color }} />}
                </Motion.div>
              </AnimatePresence>
            </Motion.button>

            <Motion.button 
              onClick={() => {
                if (onInquiry) {
                  onInquiry();
                } else {
                  const el = document.querySelector('#configurator');
                  if (el) smoothScrollIntoView(el);
                }
              }}
              className="flex-none flex items-center justify-center text-[9px] md:text-[10px] font-black uppercase tracking-[0.1em] md:tracking-[0.2em] px-4 md:px-8 bg-text-color text-bg-color rounded-[4px] transition-all duration-500 h-9 md:h-14"
              whileHover={{ 
                backgroundColor: color,
                scale: 1.05
              }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="hidden md:inline">{lt.configure}</span>
              <span className="md:hidden">Live Demo</span>
            </Motion.button>
          </div>
        </div>
      </Motion.nav>



      <main>
        {children}
      </main>

      {/* FAQ SECTION */}
      <section id="faq" className="py-20 md:py-32 bg-surface-color/5">
        <div className="max-w-[1800px] mx-auto px-6 md:px-20">
          <SectionHeading 
            title={lt.faq.title}
            highlight={lt.faq.highlight}
            color={color}
            label={lt.faq.label}
            description={lang === 'de' ? 'Alles, was du wissen musst, bevor du startest. Kein Kleingedrucktes, keine Überraschungen.' : 'Everything you need to know before you start. No fine print, no surprises, just clarity.'}
            centered={true}
          />

          <div className="w-full">
            {(faqItems || lt.faq.items).map((item, idx) => (
              <FAQItem key={idx} item={item} color={color} />
            ))}
          </div>
        </div>
      </section>

      {/* CROSS-NAVIGATION FOOTER GRID - HIDDEN */}
      {false && <section id="explore" className="py-20 md:py-32 bg-surface-color/5">
        <div className="max-w-[1800px] mx-auto px-6 md:px-20">
          <SectionHeading 
            title={lt.explore.title}
            highlight={lt.explore.highlight}
            color={color}
            label={lt.explore.label}
            description={lang === 'de' ? 'Entdecke weitere KI-gestützte Lösungen, die dein Business auf das nächste Level heben.' : 'Discover more AI-powered solutions engineered to scale your business and unlock new revenue streams.'}
            centered={true}
          />
          <Motion.div 
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false }}
            className="grid gap-6"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}
          >
            {campaigns.filter(c => c.id !== currentId).map((campaign, idx) => (
              <Motion.div 
                key={idx}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 }
                }}
                whileHover={{ 
                  y: -10, 
                  borderColor: campaign.color || color,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                onClick={() => setView(getCampaignView(campaign.id))}
                className="group cursor-pointer bg-white/[0.03] border border-border-color p-8 rounded-[4px]"
              >
                <div className="flex justify-between items-start mb-6">
                  <div className="p-3 rounded-[4px] bg-surface-color" style={{ color: campaign.color || color }}>
                    {campaign.icon}
                  </div>
                  <ArrowRight 
                    size={20} 
                    className="opacity-0 group-hover:opacity-100 -translate-x-4 group-hover:translate-x-0 transition-all duration-300" 
                    style={{ color: campaign.color || color }}
                  />
                </div>
                <h4 className="text-xl font-black mb-2 tracking-tight">{campaign.title}</h4>
                <p className="text-sm text-text-muted font-light leading-relaxed mb-8">{campaign.tagline}</p>
                
                <div className="space-y-4 mt-auto">
                  <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
                    <Motion.div 
                      initial={{ width: 0 }}
                      whileInView={{ width: `${(campaign.slots / (campaign.total || 5)) * 100}%` }}
                      className="h-full"
                      style={{ backgroundColor: campaign.color || color }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                    <span className="text-text-muted opacity-40">Inventory</span>
                    <span style={{ color: campaign.color || color }}>{campaign.slots} / {campaign.total || 5} Slots</span>
                  </div>
                </div>
              </Motion.div>
            ))}
          </Motion.div>
        </div>
      </section>}

      {/* FOOTER */}
      <footer className="py-10 border-t border-border-color bg-bg-color">
        <div className="w-full px-[8vw] flex flex-col items-center gap-6 md:flex-row md:gap-0">
          <Logo theme={theme} activeTab={category?.toLowerCase().includes('ai') ? 'ai' : 'design'} forceFull className="h-7 w-auto md:flex-shrink-0" />
          <div className="flex flex-wrap justify-center gap-6 text-[10px] font-bold tracking-widest uppercase md:absolute md:left-1/2 md:-translate-x-1/2">
            <button onClick={() => setView('impressum')} style={{ '--footer-hover-color': color }} className="hover:opacity-100 hover:scale-110 hover:text-[var(--footer-hover-color)] transition-all duration-300">
              <h6>{lang === 'de' ? 'Impressum' : 'Imprint'}</h6>
            </button>
            <a href="https://www.linkedin.com/company/92680415" target="_blank" rel="noopener noreferrer" style={{ '--footer-hover-color': color }} className="hover:opacity-100 hover:scale-110 hover:text-[var(--footer-hover-color)] transition-all duration-300">
              <h6>LINKEDIN</h6>
            </a>
          </div>
          <h6 className="text-[10px] font-bold md:ml-auto">© 2026 CRE8NOW</h6>
        </div>
      </footer>
    </div>
  );
};

export default CampaignLayout;
